import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AUTH_API_URL } from '../config/api';
import { usePermissionsWebSocket } from '../hooks/usePermissionsWebSocket';

/** Fallback poll interval for revalidating /users/me/ if the permissions WebSocket
 * is down (e.g. dropped and failed to reconnect). The socket is the primary path;
 * this just bounds the worst-case staleness window. */
const PERMISSIONS_POLL_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

const AuthContext = createContext();
//const AUTH_API_URL = 'http://127.0.0.1:8000/users/'
// Helper to get CSRF token from cookies
function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

// Configure axios globally to send credentials (cookies) by default
axios.defaults.withCredentials = true;

// Add request interceptor to attach the CSRF token and member_email dynamically
axios.interceptors.request.use(function (config) {
    // Only add CSRF token to non-GET requests (unsafe methods)
    if (config.method !== 'get' && !/^(HEAD|OPTIONS|TRACE)$/i.test(config.method)) {
        const csrfToken = getCookie('csrftoken');
        if (csrfToken) {
            config.headers['X-CSRFToken'] = csrfToken;
        } else {
            // Optional: You can remove this console.error now that CSRF is working
            // console.error("AXIOS INTERCEPTOR: CSRF token is MISSING on POST request to:", config.url);
        }
    }
    
    // Add member_email to request data for API calls (not auth calls)
    if (config.url && config.url.includes('/api/')) {
        // Get user from localStorage or context if available
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                const user = JSON.parse(userData);
                if (user && user.email) {
                    // For requests with data (POST, PUT, PATCH)
                    if (config.data && typeof config.data === 'object') {
                        // Only add member_email if it doesn't already exist in the payload
                        // This allows endpoints like bulk_update_rates to specify the member
                        if (!config.data.member_email) {
                            config.data.member_email = user.email;
                        }
                    }
                    // For DELETE requests, create data object if it doesn't exist
                    else if (config.method === 'delete') {
                        config.data = { member_email: user.email };
                    }
                }
            } catch (e) {
                // Ignore parsing errors
            }
        }
    }
    
    return config;
}, function (error) {
    return Promise.reject(error);
});


export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

    // Function to fetch user details (checks for active session cookie)
    const fetchUserDetails = useCallback(async () => {
        try {
            console.log('AUTH LOG: Fetching user details from:', `${AUTH_API_URL}me/`);
            console.log('AUTH LOG: Cookies available:', document.cookie);
            console.log('AUTH LOG: Axios withCredentials:', axios.defaults.withCredentials);
            
            const response = await axios.get(`${AUTH_API_URL}me/`, {
                withCredentials: true, // Explicitly ensure credentials are sent
            });
            
            console.log('AUTH LOG: User details fetched successfully:', response.data);
            setUser(response.data);
            // Store user data in localStorage for axios interceptor
            localStorage.setItem('user', JSON.stringify(response.data));
            return response.data; // Return user data on success
        } catch (error) {
            console.error('AUTH LOG: fetchUserDetails error:', error);
            console.error('AUTH LOG: Error response:', error.response);
            console.error('AUTH LOG: Error status:', error.response?.status);
            console.error('AUTH LOG: Error data:', error.response?.data);
            
            if (error.response?.status === 401) {
                console.warn("AUTH LOG: Session check failed - 401 Unauthorized");
                console.warn("AUTH LOG: This means the session cookie was not sent or is invalid");
                // A 401 from /users/me/ is the server's definite answer: there is
                // no session. Drop the stored user so ProtectedRoute sends the tab
                // to /login — keeping it left the shell on screen with every data
                // call refused (seen after the 2026-10-13 forced sign-out).
                setUser(null);
                localStorage.removeItem('user');
            } else if (error.request) {
                console.error('AUTH LOG: No response received from server.');
            } else {
                console.error("AUTH LOG: Error during session check:", error.message);
            }

            // Any other failure (network drop, 5xx) may be temporary: keep the user.
            return null; // Return null on error
        }
    }, []); // Empty dependency array means this function is stable

  const isAuthenticated = !!user;

  // Primary path: per-user WebSocket push. When a Developer adds/changes/removes this
  // user's TeamspaceMemberRole, the backend pushes permissions_updated and we silently
  // refetch /users/me/ -- new teamspaces, roles, and timezone changes show up without
  // the user needing to log out and back in.
  usePermissionsWebSocket(isAuthenticated, useCallback(() => {
    console.log('AUTH LOG: permissions_updated received - refetching /users/me/');
    fetchUserDetails();
  }, [fetchUserDetails]));

  // Fallback #1: revalidate when the tab regains focus (covers a dropped socket while
  // the tab was backgrounded, and the very common "left the tab open overnight" case).
  useEffect(() => {
    if (!isAuthenticated) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUserDetails();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, fetchUserDetails]);

  // Fallback #2: coarse background poll bounding worst-case staleness if both the
  // socket and focus revalidation are missed (e.g. tab never loses focus for days).
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(fetchUserDetails, PERMISSIONS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUserDetails]);

  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      try {
        // 1. Check localStorage first for quick UI update (optimistic)
        const storedUser = localStorage.getItem('user');
        let hasStoredUser = false;
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            setUser(userData); // Set user immediately from localStorage
            hasStoredUser = true;
            console.log('AUTH LOG: Restored user from localStorage:', userData.email);
          } catch (e) {
            console.warn('AUTH LOG: Failed to parse stored user:', e);
            localStorage.removeItem('user');
          }
        }

        // 2. Ensure CSRF cookie is set for later POST requests (login/logout).
        try {
          await axios.get(`${AUTH_API_URL}csrf/`, { withCredentials: true });
        } catch (e) {
          console.warn('AUTH LOG: CSRF cookie fetch failed:', e);
        }

        // 3. Verify session with server (this will update user if session is valid)
        const userData = await fetchUserDetails();
        
        if (userData) {
          // Server confirmed session is valid, user is already set by fetchUserDetails
          console.log('AUTH LOG: Session verified, user authenticated');
        } else if (hasStoredUser) {
          // The check failed. On a 401 fetchUserDetails has already cleared the
          // stored user (→ /login). Anything else (network drop, 5xx) may be
          // temporary, so the localStorage user stays and the next check decides.
          console.warn('AUTH LOG: Server session check failed (stored user kept only if it was not a 401)');
        } else {
          // No localStorage and no server session
          console.log('AUTH LOG: No stored user and no server session - user not authenticated');
          setUser(null);
        }

      } catch (error) {
        console.warn("AUTH LOG: Initial authentication setup error:", error.message);
        // If we have localStorage user but server check failed, keep it temporarily
        // The next API call will verify if session is still valid
      } finally {
        setLoading(false);
      }
    };
    initializeAuth();
  }, [fetchUserDetails]); // Add fetchUserDetails to dependency array

  const loginUser = async (email, password) => {
    console.log('AUTH LOG: Login user called with email:', email);
    console.log('AUTH LOG: AUTH_API_URL:', AUTH_API_URL);
    setLoading(true);
    try {
      console.log('AUTH LOG: Sending login request to:', `${AUTH_API_URL}login/`);
      const response = await axios.post(`${AUTH_API_URL}login/`, {
        email,
        password,
      });
      console.log('AUTH LOG: Login response:', response);
      if (response.status === 200) {
        await fetchUserDetails();
        // Simple redirect - let the routing system handle teamspace context
        navigate('/time_entries');
      } else {
        throw new Error("Login failed with status: " + response.status);
      }
    } catch (error) {
      console.log('AUTH LOG: Login error:', error);
      console.error("Login failed:", error.response?.data || error.message);
      setUser(null);
      throw new Error(error.response?.data?.error || "Login failed. Check email and password.");
    } finally {
      setLoading(false);
      console.log('AUTH LOG: Login finally block executed');
    }
  };

  // "Sign in with Google": the backend verifies the Google ID token and opens
  // the same session as the password login, so the rest of the flow is shared.
  const loginWithGoogle = async (credential) => {
    setLoading(true);
    try {
      await axios.post(`${AUTH_API_URL}google-login/`, { credential });
      await fetchUserDetails();
      navigate('/time_entries');
    } catch (error) {
      console.error("Google login failed:", error.response?.data || error.message);
      setUser(null);
      throw new Error(error.response?.data?.error || "Google sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logoutUser = async () => {
    setLoading(true);
    try {
      // Attempt to call logout endpoint to clear server-side session
      await axios.post(`${AUTH_API_URL}logout/`);
    } catch (error) {
      // Even if logout endpoint fails (e.g., session already expired), 
      // we still want to clear local state and redirect
      console.error("Logout endpoint call failed:", error);
    }
    
    // Always clear user state and localStorage regardless of endpoint success/failure
    setUser(null);
    localStorage.removeItem('user'); // Clear user data on logout
    
    // Force a full page reload to ensure all state is cleared and session cookie is removed
    // This prevents any race conditions with useEffect hooks that might re-fetch user data
    window.location.href = '/login';
  };

  // Merge a local change (e.g. a new profile_picture_version) into the user
  // without a /users/me/ round trip; the next revalidation overwrites it anyway.
  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);

  const contextData = {
    user,
    isAuthenticated,
    loading,
    loginUser,
    loginWithGoogle,
    logoutUser,
    updateUser,
    role_dict: user?.role_dict || {},
  };

  return (
    <AuthContext.Provider value={contextData}>
      {/* Always render children - loading state is handled by individual components */}
      {children}
    </AuthContext.Provider>
  );
};
export const useAuth = () => useContext(AuthContext);